import { useCallback, useMemo, useState } from 'react';
import { Uploader } from '../lib/Uploader';
import { uploadsLogger } from '../loggers';
import { UploadMetadata } from '../values/UploadMetadata';

type DropTargetProps = {
  isDragging: boolean;
  handleDragOver: (event) => void;
  handleDragLeave: (event) => void;
  handleDrop: (event) => void;
  handleClick: (event) => void;
};

const isFileTransfer = (event) =>
  event.dataTransfer.types?.some((type) => type === 'Files') ?? false;

export const useDropTarget = ({
  uploader,
}: {
  uploader: Uploader;
}): DropTargetProps => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((event) => {
    if (!isFileTransfer(event)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    // Add a small (+) icon on drop
    // (and prevent browsers from interpreting this as files being _moved_ into the browser,
    // https://github.com/transloadit/uppy/issues/1978)
    event.dataTransfer.dropEffect = 'copy'; // eslint-disable-line no-param-reassign

    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((event) => {
    if (!isFileTransfer(event)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    setIsDragging(false);
  }, []);

  const addFiles = useCallback(
    (files: File[], trigger: UploadMetadata['trigger']) => {
      if (files.length > 0) {
        uploadsLogger(`[useDropTarget] ${files.length} files were dropped`);
        uploader.addFiles(files, {
          source: { name: 'drag-and-drop' },
          accountId: 'todo-account-id',
          organizationId: 'todo-organization-id',
          trigger,
        });
      }
    },
    [uploader]
  );

  const handleDrop = useCallback(
    (event) => {
      if (!isFileTransfer(event)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      // Remove dragover class
      setIsDragging(false);

      // @TODO: consider using getDroppedFiles approach
      // @see https://github.com/transloadit/uppy/tree/main/packages/%40uppy/utils/src/getDroppedFiles
      const files: File[] = Array.from(event.dataTransfer.files);
      addFiles(files, 'drop');
    },
    [addFiles]
  );

  const handleClick = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      // @TODO: consider using getDroppedFiles approach
      // @see https://github.com/transloadit/uppy/tree/main/packages/%40uppy/utils/src/getDroppedFiles
      const files: File[] = Array.from(event.target.files);
      addFiles(files, 'click');
    },
    [addFiles]
  );

  return useMemo(() => {
    return {
      isDragging,
      handleDragOver,
      handleDragLeave,
      handleDrop,
      handleClick,
    };
  }, [isDragging, handleDragOver, handleDragLeave, handleDrop, handleClick]);
};
